<script setup lang="ts">
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import DonutChart from '../chart-donut/DonutChart.vue'

interface ChartDataItem {
  name: string
  value: number
  color: string
}

interface AgenceData {
  agence_title: string
  data: ChartDataItem[]
}

const props = withDefaults(
  defineProps<{
    loading?: boolean
    data: AgenceData[]
    currentIndex: number
    itemsPerPage: number
    canGoLeft: boolean
    canGoRight: boolean
    valueFormatter: (value: number) => string
    totalLabel?: string
    showCurrency?: boolean
  }>(),
  {
    loading: false,
    totalLabel: 'Total',
    showCurrency: false,
  },
)

const emit = defineEmits<{
  (e: 'navigate', direction: 'left' | 'right'): void
}>()
</script>

<template>
  <div>
    <div
      v-if="loading"
      class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="w-[180px] h-[180px] rounded-full bg-gray-200 animate-pulse aspect-square"
      />
    </div>

    <div v-else class="relative">
      <!-- Legend -->
      <div v-if="data.length > 0" class="flex justify-end gap-8 pr-5 mt-5">
        <div v-for="item in data[0].data" :key="item.name" class="flex items-center gap-2">
          <div :style="{ backgroundColor: item.color }" class="w-3 h-3 rounded-full" />
          <span class="text-sm">{{ item.name }}</span>
        </div>
      </div>

      <!-- Chart Grid -->
      <div class="flex items-center">
        <button
          @click="emit('navigate', 'left')"
          :disabled="!canGoLeft"
          class="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed -ml-2"
        >
          <ChevronLeft class="w-6 h-6" />
        </button>

        <div class="overflow-hidden flex-1">
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8">
            <div
              v-for="agence in data.slice(
                currentIndex * itemsPerPage,
                (currentIndex + 1) * itemsPerPage,
              )"
              :key="agence.agence_title"
              class="flex flex-col items-center"
            >
              <div class="relative">
                <DonutChart
                  :data="agence.data"
                  :width="180"
                  :height="180"
                  :show-tooltip="true"
                  :value-formatter="valueFormatter"
                  :show-currency="showCurrency"
                  :total-label="totalLabel"
                />
              </div>
              <h3 class="text-base font-semibold mt-1">{{ agence.agence_title }}</h3>
            </div>
          </div>
        </div>

        <button
          @click="emit('navigate', 'right')"
          :disabled="!canGoRight"
          class="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed -mr-2"
        >
          <ChevronRight class="w-6 h-6" />
        </button>
      </div>
    </div>
  </div>
</template>
